(function () {
  const HOVER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');

  class BtReelItem extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.bt-reel__video');
      if (!this.video) return;

      if (this.hasAttribute('data-autoplay')) {
        this.setupAutoplay();
        return;
      }

      this.boundMouseEnter = this.handleMouseEnter.bind(this);
      this.boundMouseLeave = this.handleMouseLeave.bind(this);
      this.boundClick = this.handleClick.bind(this);

      if (HOVER_QUERY.matches) {
        this.addEventListener('mouseenter', this.boundMouseEnter);
        this.addEventListener('mouseleave', this.boundMouseLeave);
      } else {
        this.addEventListener('click', this.boundClick);
      }

      HOVER_QUERY.addEventListener('change', this.handleHoverCapabilityChange.bind(this));
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }

      if (this.video && this.clipEnabled) {
        this.video.removeEventListener('loadedmetadata', this.boundLoadedMetadata);
        this.video.removeEventListener('timeupdate', this.boundTimeUpdate);
        this.video.removeEventListener('ended', this.boundClipEnded);
      }

      this.removeEventListener('mouseenter', this.boundMouseEnter);
      this.removeEventListener('mouseleave', this.boundMouseLeave);
      this.removeEventListener('click', this.boundClick);
      this.pause();
    }

    setupAutoplay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.clipDuration = parseFloat(this.dataset.clipDuration) || 0;
      this.clipEnabled = this.hasAttribute('data-clip-preview') && this.clipDuration > 0;
      this.clipPhase = 'start';

      if (this.clipEnabled) {
        this.video.loop = false;
        this.boundTimeUpdate = this.handleClipTimeUpdate.bind(this);
        this.boundLoadedMetadata = this.handleClipLoadedMetadata.bind(this);
        this.boundClipEnded = this.handleClipEnded.bind(this);
        this.video.addEventListener('loadedmetadata', this.boundLoadedMetadata);
        this.video.addEventListener('timeupdate', this.boundTimeUpdate);
        this.video.addEventListener('ended', this.boundClipEnded);
      }

      this.classList.add('is-playing');

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.play();
          } else {
            this.pause();
          }
        });
      }, { threshold: 0.35 });

      this.observer.observe(this);
      this.play();
    }

    handleClipLoadedMetadata() {
      this.videoDuration = this.video.duration;

      if (this.clipEnabled && this.videoDuration <= this.clipDuration * 2) {
        this.clipEnabled = false;
        this.video.loop = true;
      }
    }

    handleClipTimeUpdate() {
      if (!this.clipEnabled || !this.classList.contains('is-playing')) return;

      if (this.clipPhase === 'start' && this.video.currentTime >= this.clipDuration) {
        const duration = this.videoDuration || this.video.duration;
        if (!duration || !Number.isFinite(duration)) return;

        this.clipPhase = 'end';
        this.video.currentTime = Math.max(0, duration - this.clipDuration);

        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
    }

    handleClipEnded() {
      if (!this.clipEnabled || !this.classList.contains('is-playing')) return;

      this.clipPhase = 'start';
      this.video.currentTime = 0;
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    handleHoverCapabilityChange(event) {
      this.removeEventListener('mouseenter', this.boundMouseEnter);
      this.removeEventListener('mouseleave', this.boundMouseLeave);
      this.removeEventListener('click', this.boundClick);
      this.pause();

      if (event.matches) {
        this.addEventListener('mouseenter', this.boundMouseEnter);
        this.addEventListener('mouseleave', this.boundMouseLeave);
      } else {
        this.addEventListener('click', this.boundClick);
      }
    }

    handleMouseEnter() {
      this.play();
    }

    handleMouseLeave() {
      this.pause();
    }

    handleClick(event) {
      const link = this.querySelector('a[href]');

      if (link && this.classList.contains('is-playing')) {
        return;
      }

      if (link) {
        event.preventDefault();
      }

      if (this.classList.contains('is-playing')) {
        this.pause();
      } else {
        this.play();
      }
    }

    play() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.classList.add('is-playing');

      if (this.clipEnabled) {
        const duration = this.videoDuration || this.video.duration || 0;

        if (this.clipPhase === 'end' && duration > this.clipDuration) {
          this.video.currentTime = Math.max(0, duration - this.clipDuration);
        } else {
          this.clipPhase = 'start';
          this.video.currentTime = 0;
        }
      }

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    pause() {
      this.classList.remove('is-playing');
      this.video.pause();

      if (this.clipEnabled) {
        this.clipPhase = 'start';
      }

      this.video.currentTime = 0;
    }
  }

  if (!customElements.get('bt-reel-item')) {
    customElements.define('bt-reel-item', BtReelItem);
  }
})();
